import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  STAGE_ORDER,
  STAGE_BAR_COLOR,
  DELIVERABLES,
  DELIVERABLE_CONFIG,
  getStatusLabel,
  type StatusFilter,
} from "@/features/properties/services/deliveryStatusConfig";
import type { PortfolioProgress as PortfolioProgressData } from "@/features/properties/services/clientPropertiesQueries";
import { usePropertyProgressUiStore } from "@/features/properties/services/propertyProgressUiStore";
import { SegmentedProgressBar } from "./SegmentedProgressBar";
import { DeliverableBreakdown } from "./DeliverableBreakdown";

interface Props {
  clientId: string;
  progress: PortfolioProgressData | null;
  onStageClick: (stage: StatusFilter) => void;
}

/** Compact stage labels for the stat row (full "Survey Scheduled" is too wide 4-up). */
const STAT_LABEL: Record<StatusFilter, string> = {
  delivered: "Delivered",
  in_progress: "In Progress",
  survey_scheduled: "Scheduled",
  no_survey_scheduled: "Pending",
};

/**
 * Portfolio progress hero band. The four stage counts lead as the headline (each
 * filters the table on click); the segmented bar beneath ties them into one
 * whole-portfolio picture; deliverable completion sits below. Whole-portfolio —
 * ignores the active filters.
 */
export function PortfolioProgress({ clientId, progress, onStageClick }: Props) {
  const collapsed = usePropertyProgressUiStore((s) => s.collapsedByClientId[clientId] ?? false);
  const toggle = usePropertyProgressUiStore((s) => s.toggleCollapsed);

  if (!progress || progress.total === 0) return null;

  const { total, stages, deliverables } = progress;

  const segments = STAGE_ORDER.map((s) => ({
    key: s,
    label: getStatusLabel(s),
    count: stages[s] ?? 0,
    colorClass: STAGE_BAR_COLOR[s],
  }));

  const items = DELIVERABLES.map((k) => ({
    key: k,
    label: DELIVERABLE_CONFIG[k].label,
    pct: Math.round(((deliverables[k] ?? 0) / total) * 100),
  }));

  return (
    <div className="glass-panel mb-3 flex-shrink-0 rounded-xl px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
          {segments.map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid="progress-stat"
              data-stage={s.key}
              onClick={() => onStageClick(s.key)}
              className="group flex items-center gap-2 text-left"
              title={`Filter to ${STAT_LABEL[s.key]}`}
            >
              <span className={cn("h-2 w-2 shrink-0 rounded-full", s.colorClass)} />
              <span className="text-xl font-bold leading-none text-foreground">
                {s.count.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground group-hover:text-foreground">
                {STAT_LABEL[s.key]}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand portfolio progress" : "Collapse portfolio progress"}
          onClick={() => toggle(clientId)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
      </div>

      {!collapsed && (
        <div className="mt-3 space-y-3">
          <SegmentedProgressBar
            segments={segments}
            total={total}
            showLegend={false}
            onSegmentClick={(key) => onStageClick(key as StatusFilter)}
          />
          <div className="border-t border-border/50 pt-3">
            <DeliverableBreakdown items={items} />
          </div>
        </div>
      )}
    </div>
  );
}
